import { Container, Graphics } from 'pixi.js';
import { TILE_HALF } from '../sim/projection';
import { DIAMOND, TilePinnedOverlay, type Tile, type Transient } from './tilePinnedOverlay';

/**
 * The on-screen **spawn telegraph** for Blackthorn's Storm (PRD #289 Slice E,
 * ADR-0152): a ~2-second electric sparkle on the ground tile an energy vortex is
 * about to materialize on, giving players a beat to clear the spot. The presentation
 * half of the {@link ../net/protocol.StormTelegraphMessage} broadcast (Slice D) - the
 * server decides *when and where* a summon lands and telegraphs it; this overlay just
 * draws the danger cue and ages it out. A {@link TilePinnedOverlay} (Chassis Slice H):
 * a self-dismissing tile-pinned graphic, aged each frame from the render clock, drawn
 * as the 2:1 dimetric tile diamond via the shared projection so it sits squarely on
 * the tile.
 *
 * Each sparkle is a **contracting warning ring** (a tile diamond that shrinks from
 * oversized to tile-sized as the countdown closes, so the danger visibly "arrives")
 * plus a **twinkling four-point star** at the tile centre (the incoming energy),
 * fading in over the first fraction of life and fading out at the end. Its lifetime is
 * driven by the telegraph's `ticksUntil` so the sparkle winks out exactly as the
 * vortex appears.
 *
 * Storm telegraphs are always **ground-level** (the escalation summons the server
 * telegraphs land on the bank floor, z 0 - the message carries no z), so this overlay
 * deliberately leaves the base's z-aware `landZ` seam unwired and the sparkle projects
 * at elevation 0 like the autowalk marker does on a flat map. {@link GameRenderer}
 * owns one instance, ages it each frame via {@link update}, and triggers it from
 * `onStormTelegraph`.
 */

/** Electric cyan - the energy-vortex read - for the sparkle ring + star. */
const SPARKLE_COLOR = 0x8be0ff;
/** A hotter core tint for the star centre, so the spark reads as bright energy. */
const CORE_COLOR = 0xe8f7ff;

/** Fallback lifetime when the telegraph carries no usable countdown (~2s at 50ms/tick). */
const DEFAULT_TTL_MS = 2000;
/** The warning ring starts this many times tile-sized and contracts to 1x as the vortex nears. */
const RING_START_SCALE = 2.2;
/** Fraction of the lifetime spent fading the sparkle in before it holds then fades out. */
const FADE_IN_FRACTION = 0.25;
/** Twinkle pulses per second on the star's alpha, so the spark shimmers rather than sits flat. */
const TWINKLE_HZ = 3;

/** One live telegraph sparkle, aged and culled by {@link TelegraphOverlay.update}. */
interface Sparkle extends Transient {
  /** The contracting warning ring. */
  ring: Graphics;
  /** The twinkling centre star. */
  star: Graphics;
}

/** Build the four-point star (spark) drawn at the tile centre. */
function star(): Graphics {
  const g = new Graphics();
  const long = TILE_HALF * 0.9;
  const short = TILE_HALF * 0.22;
  // A four-point star: alternating long/short radii around the centre.
  g.poly([0, -long, short, -short, long, 0, short, short, 0, long, -short, short, -long, 0, -short, -short])
    .fill({ color: CORE_COLOR, alpha: 0.95 })
    .stroke({ width: 1.5, color: SPARKLE_COLOR, alpha: 1 });
  return g;
}

export class TelegraphOverlay extends TilePinnedOverlay<Sparkle> {
  /**
   * Play a spawn-telegraph sparkle on the ground `tile`, lasting `ttlMs` (clamped to a
   * sane minimum, defaulting to ~2s when the caller has no countdown). It self-dismisses
   * on the render clock; a fresh call adds another sparkle (a second telegraph mid-fade
   * simply overlaps), so this never needs an explicit clear.
   */
  telegraph(tile: Tile, ttlMs: number = DEFAULT_TTL_MS): void {
    const c = this.centre(tile);
    const container = new Container();
    container.position.set(c.x, c.y);
    const ring = new Graphics();
    ring.poly(DIAMOND).stroke({ width: 3, color: SPARKLE_COLOR, alpha: 1 });
    const s = star();
    container.addChild(ring, s);
    this.addChild(container);
    this.addTransient({
      gfx: container,
      ring,
      star: s,
      ageMs: 0,
      ttlMs: Math.max(200, ttlMs),
    });
  }

  /** How many telegraph sparkles are currently animating (for the renderer tests). */
  get activeCount(): number {
    return this.transients.length;
  }

  /**
   * Advance every sparkle by `dtMs` (the render frame delta): contract the warning
   * ring toward the tile, twinkle + fade the star, and destroy any sparkle that has
   * lived out its TTL. A static refresh (`dtMs <= 0`, e.g. a headless test frame) holds
   * steady, matching the autowalk overlay.
   */
  update(dtMs: number): void {
    if (dtMs <= 0) return;

    this.ageTransients(dtMs, (s, k) => {
      // Fade in over the first fraction of life, hold, then fade out toward the end so
      // the sparkle blooms and vanishes as the vortex lands.
      const envelope = k < FADE_IN_FRACTION ? k / FADE_IN_FRACTION : 1 - (k - FADE_IN_FRACTION) / (1 - FADE_IN_FRACTION);
      s.gfx.alpha = Math.max(0, envelope);
      // The warning ring contracts from oversized to tile-sized as the countdown closes.
      s.ring.scale.set(1 + (1 - k) * (RING_START_SCALE - 1));
      // The centre star twinkles (a fast alpha shimmer over the base envelope) and turns
      // slowly, so the spark reads as live energy rather than a static marker.
      const twinkle = 0.6 + 0.4 * Math.abs(Math.sin((s.ageMs / 1000) * TWINKLE_HZ * Math.PI));
      s.star.alpha = twinkle;
      s.star.rotation += dtMs * 0.002;
    });
  }
}
